/**
 * TASK-12181 — Filas de chamado da Organização (spec 2026-10-02-atendimento-por-organizacao §6).
 *
 * Aba do card da Organização, no padrão dos Times de SOC: o gestor (ou o admin) cria, renomeia,
 * desativa e escolhe a fila padrão, e põe e tira membros. O backend decide tudo
 * (`chamados/filas_service.py`); aqui só não se oferece o que ele recusaria: o alcance "externa"
 * só aparece para admin, e o seletor de membros lista só os usuários da Organização.
 */
import { useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Group, Loader, Modal, Select, Stack, Switch, Table, Text,
  TextInput, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPencil, IconPlus, IconStar, IconX } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { organizationsApi } from '@/api/organizations'
import { formatApiError } from '@/api/errors'

export interface FilaMembro { user_id: number; email: string; nome: string | null }
export interface Fila {
  id: number
  organization_id: number
  nome: string
  alcance: 'interna' | 'externa'
  padrao: boolean
  is_active: boolean
  membros: FilaMembro[]
}

const MENSAGENS: Record<string, string> = {
  sem_poder: 'Só o gestor da Organização ou um administrador mexe nas filas.',
  alcance_externa_so_admin: 'Só um administrador abre uma fila para atender outras Organizações.',
  usuario_fora_da_org: 'Essa pessoa não é da Organização. Adicione-a em Colaboradores antes.',
  fila_padrao_obrigatoria: 'A Organização precisa de uma fila padrão. Marque outra como padrão antes.',
  fila_inativa: 'Uma fila desativada não pode ser a padrão.',
  nome_obrigatorio: 'Dê um nome à fila.',
  nome_em_uso: 'Já existe uma fila ativa com esse nome.',
  fila_nao_encontrada: 'Essa fila não existe mais. Recarregue a página.',
}

/** Mensagem em português para o `detail` de uma recusa da API de filas. */
export function mensagemDaFila(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return (typeof detail === 'string' && MENSAGENS[detail]) || formatApiError(err, fallback)
}

const ROTULO_ALCANCE = { interna: 'Atende os tenants da Organização', externa: 'Atende qualquer tenant' }

export function AbaFilas({ orgId, isAdmin }: { orgId: number; isAdmin: boolean }) {
  const qc = useQueryClient()
  const chave = ['org-filas', orgId]
  const { data: filas = [], isLoading, isError, error } = useQuery<Fila[]>({
    queryKey: chave,
    queryFn: async () => (await api.get(`/v1/organizations/${orgId}/filas`)).data.items,
  })
  const { data: orgUsers = [] } = useQuery({
    queryKey: ['org-users', orgId],
    queryFn: () => organizationsApi.listOrgUsers(orgId),
  })

  const [criando, setCriando] = useState(false)
  const [editando, setEditando] = useState<Fila | null>(null)

  const falhou = (fallback: string) => (err: unknown) =>
    notifications.show({ color: 'red', title: 'Não foi possível', message: mensagemDaFila(err, fallback) })
  const recarregar = () => qc.invalidateQueries({ queryKey: chave })

  const alterar = useMutation({
    mutationFn: ({ id, ...corpo }: { id: number; nome?: string; padrao?: boolean; is_active?: boolean }) =>
      api.patch(`/v1/organizations/${orgId}/filas/${id}`, corpo),
    onSuccess: () => { recarregar(); setEditando(null) },
    onError: falhou('Falha ao alterar a fila.'),
  })
  const porMembro = useMutation({
    mutationFn: ({ fila, user }: { fila: number; user: number }) =>
      api.put(`/v1/organizations/${orgId}/filas/${fila}/membros/${user}`),
    onSuccess: recarregar,
    onError: falhou('Falha ao pôr o membro.'),
  })
  const tirarMembro = useMutation({
    mutationFn: ({ fila, user }: { fila: number; user: number }) =>
      api.delete(`/v1/organizations/${orgId}/filas/${fila}/membros/${user}`),
    onSuccess: recarregar,
    onError: falhou('Falha ao tirar o membro.'),
  })

  if (isLoading) return <Loader size="sm" />
  if (isError) {
    return <Alert color="red" variant="light">{mensagemDaFila(error, 'Não foi possível carregar as filas.')}</Alert>
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          O chamado chega numa fila; quem é membro dela o vê na Entrada, em Triagem (Minhas filas na Entrada de abas).
        </Text>
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setCriando(true)}>
          Nova fila
        </Button>
      </Group>

      {filas.length === 0 ? (
        <Text size="sm" c="dimmed">Esta Organização ainda não tem fila. Sem fila, ela não recebe chamado.</Text>
      ) : (
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Fila</Table.Th>
              <Table.Th>Alcance</Table.Th>
              <Table.Th>Membros</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filas.map((f) => {
              const fora = orgUsers
                .filter((u) => !f.membros.some((m) => m.user_id === u.user_id))
                .map((u) => ({ value: String(u.user_id), label: u.full_name || u.email }))
              return (
                <Table.Tr key={f.id} style={f.is_active ? undefined : { opacity: 0.55 }}>
                  <Table.Td>
                    <Group gap={6}>
                      <Text size="sm" fw={500}>{f.nome}</Text>
                      {f.padrao && <Badge size="xs" color="inovareTeal">padrão</Badge>}
                      {!f.is_active && <Badge size="xs" color="gray">desativada</Badge>}
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="xs">{ROTULO_ALCANCE[f.alcance]}</Text></Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {f.membros.map((m) => (
                        <Badge
                          key={m.user_id} variant="light" size="sm"
                          rightSection={
                            <ActionIcon
                              size="xs" variant="transparent" aria-label={`Tirar ${m.nome || m.email} da fila`}
                              onClick={() => tirarMembro.mutate({ fila: f.id, user: m.user_id })}
                            >
                              <IconX size={10} />
                            </ActionIcon>
                          }
                        >
                          {m.nome || m.email}
                        </Badge>
                      ))}
                      {f.is_active && fora.length > 0 && (
                        <Select
                          size="xs" w={180} placeholder="Pôr membro" searchable data={fora} value={null}
                          aria-label={`Pôr membro na fila ${f.nome}`}
                          onChange={(v) => v && porMembro.mutate({ fila: f.id, user: Number(v) })}
                        />
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      {f.is_active && !f.padrao && (
                        <Tooltip label="Tornar padrão">
                          <ActionIcon variant="subtle" aria-label={`Tornar ${f.nome} a fila padrão`}
                                      onClick={() => alterar.mutate({ id: f.id, padrao: true })}>
                            <IconStar size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Renomear">
                        <ActionIcon variant="subtle" aria-label={`Renomear ${f.nome}`} onClick={() => setEditando(f)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Button
                        size="compact-xs" variant="subtle" color={f.is_active ? 'red' : 'gray'}
                        onClick={() => alterar.mutate({ id: f.id, is_active: !f.is_active })}
                      >
                        {f.is_active ? 'Desativar' : 'Reativar'}
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      )}

      <NovaFila orgId={orgId} isAdmin={isAdmin} opened={criando} onClose={() => setCriando(false)}
                onCriada={recarregar} temFila={filas.some((f) => f.is_active)} />
      <Modal opened={!!editando} onClose={() => setEditando(null)} title="Renomear fila" size="sm">
        {editando && (
          <Renomear atual={editando.nome} salvando={alterar.isPending}
                    onSalvar={(nome) => alterar.mutate({ id: editando.id, nome })} />
        )}
      </Modal>
    </Stack>
  )
}

function Renomear({ atual, salvando, onSalvar }: { atual: string; salvando: boolean; onSalvar: (n: string) => void }) {
  const [nome, setNome] = useState(atual)
  return (
    <Stack>
      <TextInput label="Nome" value={nome} onChange={(e) => setNome(e.currentTarget.value)} data-autofocus />
      <Group justify="flex-end">
        <Button loading={salvando} disabled={!nome.trim() || nome.trim() === atual} onClick={() => onSalvar(nome.trim())}>
          Salvar
        </Button>
      </Group>
    </Stack>
  )
}

function NovaFila({ orgId, isAdmin, opened, onClose, onCriada, temFila }: {
  orgId: number; isAdmin: boolean; opened: boolean; onClose: () => void; onCriada: () => void; temFila: boolean
}) {
  const [nome, setNome] = useState('')
  const [alcance, setAlcance] = useState<'interna' | 'externa'>('interna')
  const [padrao, setPadrao] = useState(false)
  const criar = useMutation({
    mutationFn: () => api.post(`/v1/organizations/${orgId}/filas`, { nome: nome.trim(), alcance, padrao: padrao || !temFila }),
    onSuccess: () => { onCriada(); onClose(); setNome(''); setAlcance('interna'); setPadrao(false) },
    onError: (err: unknown) =>
      notifications.show({ color: 'red', title: 'Não foi possível', message: mensagemDaFila(err, 'Falha ao criar a fila.') }),
  })
  return (
    <Modal opened={opened} onClose={onClose} title="Nova fila" size="sm">
      <Stack>
        <TextInput label="Nome" placeholder="Ex.: Banco de dados" value={nome}
                   onChange={(e) => setNome(e.currentTarget.value)} data-autofocus />
        {isAdmin && (
          <Select
            label="Alcance"
            data={[
              { value: 'interna', label: ROTULO_ALCANCE.interna },
              { value: 'externa', label: ROTULO_ALCANCE.externa },
            ]}
            value={alcance}
            onChange={(v) => setAlcance((v as 'interna' | 'externa') ?? 'interna')}
            allowDeselect={false}
          />
        )}
        <Switch
          label={temFila ? 'Fila padrão da Organização' : 'Fila padrão da Organização (a primeira fila é sempre a padrão)'}
          checked={padrao || !temFila} disabled={!temFila}
          onChange={(e) => setPadrao(e.currentTarget.checked)}
        />
        <Group justify="flex-end">
          <Button loading={criar.isPending} disabled={!nome.trim()} onClick={() => criar.mutate()}>Criar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
